
import Vue from 'vue'
import AppToolbar from '@/_components/bar/appToolbar/appToolbar.vue'
import UserTable from '@/components/userTable/UserTable.vue'
import UserAvatar from '@/_components/avatar/user/user.vue'
import DisplayContractor from './components/DisplayContractor.vue'
import { COURSE_QUERY } from './gql/queries'
import CourseCompletionDialog from './components/CourseCompletionDialog.vue'
import UserCourseCompletions from './components/UserCourseCompletions.vue'
import { ApolloQueryResult } from '@apollo/client'
import {
    CREATE_ATTESTED_COURSE_COMPLETION_MUTATION,
    CREATE_SELF_ATTESTED_COMPLETION_MUTATION,
    DELETE_COURSE_COMPLETION_MUTATION,
} from './gql/mutations'
import type { ConfirmedProjectUser, Course, PendingProjectUser, Query } from '@/schema'
import { PROJECT_USERS_QUERY } from '../gql/queries'

class UserCourseViewModel {
    projectUser: ConfirmedProjectUser | PendingProjectUser
    isUpdating = false

    get completionsByCourseId() {
        if ('aggregateCourseCompletionData' in this.projectUser) {
            return Object.groupBy(
                this.projectUser.aggregateCourseCompletionData,
                (item) => item.courseId
            )
        }

        return {}
    }

    courseCompletionsColor(id: string) {
        const completions = this.completionsByCourseId[id]

        if (!completions) {
            return 'grey'
        }

        let hasExpiredItems = false
        let hasCompletedItems = false

        completions.forEach((courseCompletion) => {
            if (courseCompletion.status === 'EXPIRED') {
                hasExpiredItems = true
            } else if (courseCompletion.status === 'COMPLETED') {
                hasCompletedItems = true
            }
        })

        if (hasExpiredItems) {
            return 'warning'
        } else if (hasCompletedItems) {
            return 'success'
        }
        return 'grey'
    }

    constructor(user: ConfirmedProjectUser | PendingProjectUser) {
        this.projectUser = Object.assign({}, user)
    }
}

export default {
    name: 'CoursePage',
    components: {
        AppToolbar,
        CourseCompletionDialog,
        DisplayContractor,
        UserCourseCompletions,
        UserTable,
        UserAvatar,
    },
    data() {
        return {
            courses: [] as Course[],
            selectedCourse: null as Course | null,
            selectedUserCourse: null as UserCourseViewModel | null,
            isFetchingCourses: false,
            isCourseCompletionDialogOpen: false,
            userCourses: [] as UserCourseViewModel[],
            menuActivatorEl: null as Maybe<string>,
            accessible: false,
            selectedMode: 'mine',
        }
    },
    computed: {
        courseSelectionItems() {
            return [
                { text: this.$t('fe_courses.my_completed_courses'), value: 'mine' },
                { text: this.$t('fe_courses.users_completed_courses'), value: 'others' },
            ]
        },
    },
    methods: {
        async handleSelectedModeChanged() {
            this.courses = []
            this.getAllCourses()
            if (this.selectedMode === 'mine') {
                await this.getCurrentUserCourse()
            }
        },
        setMenuActivator(course: Course, userCourse: UserCourseViewModel) {
            if (!('userId' in userCourse.projectUser)) {
                return
            }

            this.menuActivatorEl = `#checkbox${userCourse.projectUser.id}-${course.id}`
            setTimeout(() => {
                this.selectedCourse = course
                this.selectedUserCourse = userCourse
            })
        },
        resetMenuActivator(isOpen = false) {
            if (isOpen) return

            this.menuActivatorEl = null
            this.selectedCourse = null
            if (this.selectedMode === 'others') {
                this.selectedUserCourse = null
            }
        },
        async getCourses(params: Record<string, any> = {}) {
            this.isFetchingCourses = Object.keys(params).length === 0
            try {
                const coursesResult: ApolloQueryResult<Query> = await this.$apollo.query({
                    query: COURSE_QUERY,
                    variables: {
                        projectId: this.currentProjectId,
                        ...params,
                    },
                    fetchPolicy: 'network-only',
                })
                coursesResult.data.courses?.nodes?.forEach((course) => {
                    this.courses.push(course)
                })

                return coursesResult
            } finally {
                this.isFetchingCourses = false
            }
        },
        async getUser(userCourse: UserCourseViewModel) {
            if (!('userId' in userCourse.projectUser)) {
                return
            }

            const userResult: ApolloQueryResult<Query> = await this.$apollo.query({
                query: PROJECT_USERS_QUERY,
                variables: {
                    userId: userCourse.projectUser.userId,
                    projectId: this.currentProjectId,
                },
                fetchPolicy: 'network-only',
            })
            const user = userResult.data.projectUsers?.nodes?.[0]
            if (user) {
                const index = this.userCourses.findIndex(
                    (item) => item.projectUser.id === userCourse.projectUser.id
                )
                if (this.selectedMode === 'mine') {
                    this.selectedUserCourse = new UserCourseViewModel(user)
                } else {
                    Vue.set(this.userCourses, index, new UserCourseViewModel(user))
                }
            }
        },
        async markCourseInCompleted(userCourse: UserCourseViewModel, completionId: string) {
            if (!('userId' in userCourse.projectUser)) {
                return
            }

            await this.$apollo.mutate({
                mutation: DELETE_COURSE_COMPLETION_MUTATION,
                variables: { completionId },
            })
            this.getUser(userCourse)
            this.resetMenuActivator()
        },
        async markCourseComplete(
            course: Course,
            userCourse: UserCourseViewModel,
            commpleteDate: Maybe<string>
        ) {
            userCourse.isUpdating = true
            const variables: Record<string, any> = {
                projectId: this.currentProjectId,
                courseId: course.id,
                completedAt: commpleteDate ? new Date(commpleteDate) : null,
            }
            let document
            if (
                'userId' in userCourse.projectUser &&
                this.currentUser.id === userCourse.projectUser.userId
            ) {
                document = CREATE_SELF_ATTESTED_COMPLETION_MUTATION
            } else if ('userId' in userCourse.projectUser) {
                variables.userId = userCourse.projectUser.userId
                document = CREATE_ATTESTED_COURSE_COMPLETION_MUTATION
            }

            try {
                await this.$apollo.mutate({ mutation: document, variables })
                this.getUser(userCourse)
                this.resetMenuActivator()
            } finally {
                userCourse.isUpdating = false
            }
        },
        updateUserList(users: (ConfirmedProjectUser | PendingProjectUser)[]) {
            this.userCourses = users.map((user) => new UserCourseViewModel(user))
        },
        async getAllCourses() {
            let pageInfo
            while (pageInfo === undefined || pageInfo.hasNextPage) {
                const params: Record<string, any> = {}
                if (pageInfo?.endCursor) {
                    params.after = pageInfo.endCursor
                }
                try {
                    const coursesResult = await this.getCourses(params)
                    pageInfo = coursesResult.data?.courses?.pageInfo
                } catch (error) {
                    pageInfo = null
                    throw error
                }
            }
        },
        async getCurrentUserCourse() {
            const userResult = await this.$apollo.query({
                query: PROJECT_USERS_QUERY,
                variables: {
                    projectId: this.currentProjectId,
                    userId: this.currentUser.id,
                },
                fetchPolicy: 'network-only',
            })
            const user = userResult.data.projectUsers?.nodes?.[0]
            if (user) {
                this.selectedUserCourse = new UserCourseViewModel(user)
            }
        },
    },
    async created() {
        this.accessible = this.isModuleViewAccessible('course.')

        if (!this.accessible) {
            return
        }

        this.handleSelectedModeChanged()
    },
}
